<script lang="ts" setup>
import { WaitingSessionCreationInstance } from "@/components/mobile/chat/WaitingSessionCreation.vue";
import { useMateChatHead } from "@/composable/head/useMateChatHead";
import api from "@/modules/api";
import { useChat } from "@/stores/chat";
import { useChatSession } from "@/stores/chatSession";
import { useMate } from "@/stores/mate";
import { Mate, MateSession } from "@/types/entity";
import { useTriggerLoginForChat } from "@/use/useTriggerLogin";
import { useRouteParams } from "@vueuse/router";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useRoute } from "vue-router";

// 状态
const routeName = "mateChatScene";
const chat = useChat();
const mateStore = useMate();
const route = useRoute();
const chatSessionStore = useChatSession();
const targetMateUuid = useRouteParams("uuid", "");
const mateSessionId = useRouteParams("mateSessionId", 0, { transform: Number });
const targetMate = ref<Mate | null>(null);
useMateChatHead(targetMate);
const isLoading = ref(false);
const currentSession = ref<MateSession | null>(null);
const waitingRef = ref<WaitingSessionCreationInstance | null>(null);
const isPrivate = computed(() => {
  if (!currentSession.value) return false;
  return currentSession.value?.isPrivate === 1;
});
const showChatSetting = ref(false);
const isSceneMode = computed(() => currentSession.value?.mode === 1);
const { shouldPopLogin } = useTriggerLoginForChat(currentSession);
const mateImageList = ref<MateMedia[]>([]);
// Lifecycle
const prepareData = async () => {
  isLoading.value = true;
  try {
    currentSession.value = await chatSessionStore.getOrFetchMateSessionItem(
      mateSessionId.value
    );
    // 1. 获取聊天记录
    const messageListPromise = chat.initMessageList(mateSessionId.value);
    // 2. 获取聊天对象信息
    const targetMatePromise = mateStore.getOrFetchMate(targetMateUuid.value);
    // 3. 自拍数刷新
    const chatImageCountPromise = mateStore.refreshChatImageCount(
      targetMateUuid.value
    );
    const mateMediaPromise = api.readMateMediaPage({
      filter: {
        mateId: currentSession.value!.mateId!,
      },
      pagination: { page: 1, perPage: 9 },
      sort: { field: "id", order: "desc" },
    });
    const [mate, { page }] = await Promise.all([
      targetMatePromise,
      mateMediaPromise,
      messageListPromise,
      chatImageCountPromise,
    ]);
    targetMate.value = mate as unknown as Mate;
    mateImageList.value = page.data;
  } finally {
    isLoading.value = false;
  }
};
onBeforeMount(prepareData);
watch([targetMateUuid, mateSessionId], () => {
  if (route.name === routeName) {
    prepareData();
  }
});
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <div>
      <chat-header
        :current-session="currentSession!"
        :is-loading="isLoading"
        :is-private="isPrivate"
        :is-scene-mode="isSceneMode"
        :mate="targetMate!"
      />
    </div>
    <!-- 内容区 -->
    <div
      v-if="isLoading"
      class="w-full h-full flex items-center justify-center"
    >
      <span class="loading loading-sm"></span>
    </div>
    <chat-text-mode
      v-else
      :mate="targetMate!"
      :mate-session-id="mateSessionId"
      :mateImageList="mateImageList"
      :should-login="shouldPopLogin"
    />
    <chat-setting
      v-if="targetMate && isPrivate"
      v-model:show="showChatSetting"
      :session-id="mateSessionId"
      :target-mate="targetMate"
    />
    <waiting-session-creation ref="waitingRef" />
  </div>
</template>
<style scoped>
.chat-mode-toggle {
  --tw-border-opacity: 1;
  --tw-bg-opacity: 1;
}
</style>
